import { useCallback, useLayoutEffect } from 'react';
import { identity, isEmpty, isEqual, last } from 'lodash';
import { FORM_MODES } from 'config/formModes';
import { useDrawerStack } from 'hooks/queryParams/useDrawerStack';
import { useMultiLayerDrawerEnabled } from 'modules/taboola-common-frontend-modules/multi-layer-drawer/hooks/useMultiLayerDrawerEnabled';
import { matchPathWithoutQuery } from '../../query-params';
import { useNavigationBlocker } from './useNavigationBlocker';

export const useUnsavedChangesController = ({
    formPath = '',
    hasDataChanges,
    onBeforeConfirmCancelNavigation = identity,
    mode,
}) => {
    const isMultiLayerDrawerEnabled = useMultiLayerDrawerEnabled();
    const { stack } = useDrawerStack();
    const isLastLayer = last(stack)?.path === formPath;

    const skipBlockNavigationCondition = useCallback(
        (pathname, search) => {
            if (isMultiLayerDrawerEnabled) {
                try {
                    const params = new URLSearchParams(search);
                    const newStack = JSON.parse(params.get('drawerStack') || '[]');

                    if (newStack.length > stack.length) {
                        return isEqual(stack, newStack.slice(0, newStack.length - 1));
                    }

                    if (newStack.length === stack.length) {
                        return isEqual(stack, newStack);
                    }

                    return false;
                } catch {
                    return false;
                }
            }

            return matchPathWithoutQuery(pathname, { path: formPath }) !== null;
        },
        [formPath, isMultiLayerDrawerEnabled, stack]
    );

    const blockNavigation = useNavigationBlocker({ skipBlockNavigationCondition, onBeforeConfirmCancelNavigation });

    useLayoutEffect(() => {
        if (
            !hasDataChanges ||
            mode === FORM_MODES.PREVIEW ||
            (isMultiLayerDrawerEnabled && !isLastLayer) ||
            isEmpty(formPath)
        ) {
            return;
        }

        const unblockNavigation = blockNavigation();

        return unblockNavigation;
    }, [blockNavigation, isLastLayer, isMultiLayerDrawerEnabled, formPath, hasDataChanges, mode]);
};
